/**
 * @fileoverview Tuner tool for the redesigned Tools screen ("Studio Quiet").
 * Autocorrelation pitch detection over mic input; logic unchanged from the
 * former TunerPage, presentation restyled. Preserves tuner-page / note-display
 * / frequency-display / cents-display / toggle-tuner-button testids.
 *
 * Two hosts, two stream modes:
 *
 * - `ToolsPage` passes nothing, so the tuner opens its OWN microphone stream
 *   and owns its lifecycle — the original behaviour.
 * - The recording screen's tools panel passes the LIVE capture stream, because
 *   a second `getUserMedia` while `MediaRecorder` is running is exactly the
 *   thing we must not do: on iOS/WebKit the new capture preempts the old one,
 *   which the recorder sees as its mic track firing `ended` and honestly stops
 *   the lesson on. One stream, two consumers, no contention.
 *
 * A borrowed stream is NOT ours to stop. `stopListening` therefore only tears
 * down what this component built (the rAF loop, the source node, its own
 * AudioContext) and calls `track.stop()` solely when it opened the stream
 * itself — stopping a borrowed track would kill the recording it belongs to.
 */

import { useState, useEffect, useRef } from "react";
import { Mic, MicOff } from "lucide-react";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/**
 * @param {object} props
 * @param {MediaStream} [props.stream] - A live capture to analyse instead of
 *   opening the mic. When set, the tuner never calls getUserMedia and never
 *   stops the stream's tracks.
 */
export default function TunerTool({ stream: borrowedStream = null }) {
  const [isListening, setIsListening] = useState(false);
  const [frequency, setFrequency] = useState(null);
  const [note, setNote] = useState(null);
  const [octave, setOctave] = useState(null);
  const [cents, setCents] = useState(0);
  const [hasPermission, setHasPermission] = useState(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);
  // True only when this component opened the stream itself, and therefore may
  // stop its tracks on teardown. False for a borrowed capture (see the header).
  const ownsStreamRef = useRef(false);

  const getNote = (freq) => {
    if (!freq || freq < 20) return null;
    const semitones = 12 * Math.log2(freq / 440);
    const roundedSemitones = Math.round(semitones);
    const centsOff = Math.round((semitones - roundedSemitones) * 100);
    const noteIndex = ((roundedSemitones % 12) + 12 + 9) % 12;
    const noteOctave = Math.floor((roundedSemitones + 9) / 12) + 4;
    return { note: NOTES[noteIndex], octave: noteOctave, cents: centsOff };
  };

  const autoCorrelate = (buffer, sampleRate) => {
    const SIZE = buffer.length;
    const MAX_SAMPLES = Math.floor(SIZE / 2);
    let bestOffset = -1;
    let bestCorrelation = 0;
    let rms = 0;
    for (let i = 0; i < SIZE; i++) rms += buffer[i] * buffer[i];
    rms = Math.sqrt(rms / SIZE);
    if (rms < 0.01) return -1;
    let lastCorrelation = 1;
    for (let offset = 0; offset < MAX_SAMPLES; offset++) {
      let correlation = 0;
      for (let i = 0; i < MAX_SAMPLES; i++) correlation += Math.abs(buffer[i] - buffer[i + offset]);
      correlation = 1 - correlation / MAX_SAMPLES;
      if (correlation > 0.9 && correlation > lastCorrelation) {
        if (correlation > bestCorrelation) {
          bestCorrelation = correlation;
          bestOffset = offset;
        }
      }
      lastCorrelation = correlation;
    }
    return bestCorrelation > 0.01 && bestOffset > 0 ? sampleRate / bestOffset : -1;
  };

  const startListening = async () => {
    try {
      // Borrowed capture: analyse it in place. Opening our own would preempt
      // the recording's microphone on iOS and kill the lesson.
      const stream = borrowedStream || (await navigator.mediaDevices.getUserMedia({ audio: true }));
      streamRef.current = stream;
      ownsStreamRef.current = !borrowedStream;
      setHasPermission(true);
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current = source;
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 2048;
      const dataArray = new Float32Array(analyserRef.current.fftSize);
      const detect = () => {
        analyserRef.current.getFloatTimeDomainData(dataArray);
        const freq = autoCorrelate(dataArray, audioContextRef.current.sampleRate);
        if (freq > 0) {
          setFrequency(Math.round(freq * 10) / 10);
          const noteInfo = getNote(freq);
          if (noteInfo) {
            setNote(noteInfo.note);
            setOctave(noteInfo.octave);
            setCents(noteInfo.cents);
          }
        } else {
          setFrequency(null);
          setNote(null);
          setCents(0);
        }
        animationRef.current = requestAnimationFrame(detect);
      };
      detect();
      setIsListening(true);
    } catch (_error) {
      setHasPermission(false);
    }
  };

  const stopListening = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    sourceRef.current?.disconnect?.();
    sourceRef.current = null;
    // Only stop tracks we opened. A borrowed stream belongs to the recording,
    // and stopping it here would end the lesson capture.
    if (ownsStreamRef.current) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    ownsStreamRef.current = false;
    // The AudioContext is always ours, borrowed stream or not.
    audioContextRef.current?.close();
    audioContextRef.current = null;
    setIsListening(false);
    setFrequency(null);
    setNote(null);
    setCents(0);
  };

  useEffect(() => () => stopListening(), []);

  // The borrowed capture went away (the instructor stopped recording): the
  // analyser would sit on a dead track showing a frozen needle, so stop.
  useEffect(() => {
    // stopListening is deliberately not a dependency: it's a fresh closure each
    // render, and depending on it would tear the tuner down on every render.
    if (isListening && !ownsStreamRef.current && !borrowedStream) stopListening();
  }, [borrowedStream, isListening]);

  const needleRotation = Math.max(-45, Math.min(45, cents * 0.9));
  const isInTune = note && Math.abs(cents) < 5;

  return (
    <div className="text-center" data-testid="tuner-page">
      {hasPermission === false && (
        <p className="mb-6 text-sm text-[var(--state-danger)]">Microphone access denied</p>
      )}

      {/* Arc gauge */}
      <div className="relative mx-auto mt-2 h-[150px] w-[260px]">
        <svg viewBox="0 0 260 150" width="260" height="150" className="mx-auto">
          <path d="M20 140 A110 110 0 0 1 240 140" fill="none" stroke="var(--studio-interactive)" strokeWidth="3" strokeLinecap="round" />
          <line
            x1="130" y1="140" x2="130" y2="45"
            stroke={isInTune ? "var(--accent-yellow)" : "var(--text-tertiary)"}
            strokeWidth="2"
            strokeLinecap="round"
            style={{ transformOrigin: "130px 140px", transform: `rotate(${needleRotation}deg)`, transition: "transform 0.15s ease-out" }}
          />
          <circle cx="130" cy="140" r="6" fill={isInTune ? "var(--accent-yellow)" : "var(--text-tertiary)"} />
        </svg>
        <span className="absolute bottom-2 left-3 text-xs text-[var(--text-tertiary)]">♭</span>
        <span className="absolute bottom-2 right-3 text-xs text-[var(--text-tertiary)]">♯</span>
      </div>

      {/* Note */}
      <div
        className={`mt-2 font-serif text-7xl font-light tracking-tight ${isInTune ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-primary)]"}`}
        data-testid="note-display"
      >
        {note || "—"}
        {octave && <span className="ml-1 text-4xl text-[var(--text-tertiary)]">{octave}</span>}
      </div>

      {/* White indicator bar */}
      <div className={`mx-auto mt-6 h-[3px] w-[90px] rounded-full ${isInTune ? "bg-[var(--accent-yellow)]" : "bg-[var(--text-primary)]"}`} />

      {/* Readouts */}
      <div className="mt-8 flex justify-center gap-11">
        <div>
          <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)]">Hz</div>
          <div className="mt-1 font-mono text-[22px] text-[var(--text-secondary)]" data-testid="frequency-display">{frequency || "—"}</div>
        </div>
        <div>
          <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--text-tertiary)]">Cents</div>
          <div className={`mt-1 font-mono text-[22px] ${isInTune ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]"}`} data-testid="cents-display">
            {cents > 0 ? `+${cents}` : cents || "0"}
          </div>
        </div>
      </div>

      {/* Mic toggle */}
      <button
        onClick={isListening ? stopListening : startListening}
        data-testid="toggle-tuner-button"
        aria-label={isListening ? "Stop tuner" : "Start tuner"}
        className="mx-auto mt-8 flex h-[108px] w-[108px] items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_8px_32px_var(--accent-yellow-glow)]"
      >
        {isListening ? <MicOff className="h-9 w-9 text-[var(--text-inverse)]" strokeWidth={2} /> : <Mic className="h-9 w-9 text-[var(--text-inverse)]" strokeWidth={2} />}
      </button>
      <div className="mt-4 font-sans text-[15px] text-[var(--text-tertiary)]">
        {isListening ? (note ? (isInTune ? "In tune" : cents < 0 ? "Flat" : "Sharp") : "Listening…") : "Tap to start tuning"}
      </div>

      {/* Note reference */}
      <div className="mx-auto mt-8 flex max-w-[520px] flex-wrap justify-center gap-2.5">
        {NOTES.map((n) => (
          <div
            key={n}
            className={`flex h-[54px] w-[54px] items-center justify-center rounded-full font-sans text-[15px] transition-colors duration-150 ${
              note === n
                ? "bg-[var(--accent-yellow)] text-[var(--text-inverse)]"
                : "border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)]"
            }`}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}
